import React from 'react';
import { Card, Row, Col, Form, Badge } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { numericKeydownHandler } from '../../../utilities/common';
import { formatNumber } from '../../reports/reportHelper';

const FleetElectrificationForm = ({ baseline, mode }) => {
  const { t } = useTranslation();
  const {
    register,
    formState: { errors },
  } = useFormContext();

  const fuel = baseline?.data?.volume;
  const distance = baseline?.data?.distance;
  const fleet = baseline?.data?.fleet;

  const renderValue = (value, unit = '', defaultValue = '...') => {
    if (['', undefined, null].includes(value) || isNaN(value)) {
      return defaultValue;
    }
    return `${formatNumber(value)}${unit ? ` ${unit}` : ''}`;
  };

  return (
    <>
      <Card className="mb-4">
        <Card.Body>
          <div className="font-weight-bold mb-3">
            {t('CarbonView Data')}
          </div>

          <Row>
            <Col md={3}>
              <div className="text-muted small">
                {t('Annual Fuel Consumption')}
              </div>
              <div className="font-weight-medium">
                {renderValue(
                  fleet?.annualFuelConsumption ?? fuel?.consumption,
                  `${fuel?.unit ?? 'L'}/year`
                )}
              </div>
            </Col>

            <Col md={3}>
              <div className="text-muted small">
                {t('Annual Mileage from Distance')}
              </div>
              <div className="font-weight-medium">
                {renderValue(
                  fleet?.annualMileageFromDistance ?? distance?.consumption,
                  'km'
                )}
              </div>
            </Col>

            <Col md={3}>
              <div className="text-muted small">
                {t('Annual Mileage from Fuel')}
              </div>
              <div className="font-weight-medium">
                {renderValue(fleet?.annualMileageFromFuel, 'km', '- km')}
              </div>
            </Col>

            <Col md={3}>
              <div className="text-muted small">
                {t('Total Annual Mileage')}
              </div>
              <div className="font-weight-medium">
                {renderValue(fleet?.totalAnnualMileage, 'km')}
              </div>
            </Col>
          </Row>

          <Row className="mt-4">
            <Col md={3}>
              <div className="text-muted small">
                {t('Total Annual Emission')}
              </div>
              <div className="font-weight-medium">
                {renderValue(fleet?.totalAnnualEmission, 'tCO₂e/year')}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-1">
            <div className="font-weight-bold">
              {t('User Parameters')}
            </div>
            <Badge variant="success">
              {t('Required')}
            </Badge>
          </div>

          <div className="text-muted small mb-4">
            {t('Define electric vehicle specifications and costs')}
          </div>

          <Row>
            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  {t('EV Efficiency (kWh/km)')} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  onKeyDown={numericKeydownHandler}
                  placeholder={t('e.g., 0.2')}
                  {...register('evEfficiency')}
                  disabled={mode === 'view'}
                  isInvalid={!!errors.evEfficiency}
                />
                <Form.Text className="text-muted">
                  {t('Energy consumption per kilometer (typical: 0.15–0.25)')}
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  {errors.evEfficiency?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  {t('EV CAPEX (£)')} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder={t('e.g., 45000')}
                  onKeyDown={numericKeydownHandler}
                  {...register('evCapex')}
                  disabled={mode === 'view'}
                  isInvalid={!!errors.evCapex}
                />
                <Form.Text className="text-muted">
                  {t('Total cost to purchase electric vehicle(s)')}
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  {errors.evCapex?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  {t('Charging Cost (£/kWh)')} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  onKeyDown={numericKeydownHandler}
                  placeholder={t('e.g., 0.14')}
                  {...register('chargingCost')}
                  disabled={mode === 'view'}
                  isInvalid={!!errors.chargingCost}
                />
                <Form.Text className="text-muted">
                  {t('Cost per kWh for EV charging')}
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  {errors.chargingCost?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  {t('Fuel Price (£/L)')} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  onKeyDown={numericKeydownHandler}
                  placeholder={t('e.g., 1.45')}
                  {...register('fuelPrice')}
                  disabled={mode === 'view'}
                  isInvalid={!!errors.fuelPrice}
                />
                <Form.Text className="text-muted">
                  {t('Current fuel cost per liter')}
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  {errors.fuelPrice?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  {t('Conversion Factor (L/km)')} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  onKeyDown={numericKeydownHandler}
                  placeholder={t('e.g., 0.08')}
                  {...register('conversionFactor')}
                  disabled={mode === 'view'}
                  isInvalid={!!errors.conversionFactor}
                />
                <Form.Text className="text-muted">
                  {t('Fuel consumption per kilometer (liters per km)')}
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  {errors.conversionFactor?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  {t('EV Emission Factor (tCO₂e/km)')} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  onKeyDown={numericKeydownHandler}
                  placeholder={t('e.g., 0.05')}
                  {...register('evEmissionFactor')}
                  disabled={mode === 'view'}
                  isInvalid={!!errors.evEmissionFactor}
                />
                <Form.Text className="text-muted">
                  {t('CO₂ emissions per kilometer for electric vehicles')}
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  {errors.evEmissionFactor?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </>
  );
};

export default FleetElectrificationForm;
